/* eslint-disable no-unused-vars */
/**
=========================================================
* Trust Cargo - Users (people)
=========================================================

* Users and Customers used to be two tabs over two tables. They are one list
* of people: `users.role_id` decides whether someone is an admin or a
* customer, so that - and only that - drives the filter here.
*
* The extra customer fields (code, address, VAT name, price) still live in the
* `customers` table, so those records are joined on customer.user_id and shown
* on the rows they belong to. If a customer-role user has no such record, the
* row is flagged rather than silently rendering blanks.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DeleteUser from "./delete_user";

const API = "https://trust-cargo.co/anas_backend/api";

// the three views of the same list
const ROLE_TABS = [
  { key: "all", label: "All" },
  { key: "customer", label: "Customers" },
  { key: "admin", label: "Staff" },
];

function Users() {
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [profile, setProfile] = useState("");
  const [loading, setloading] = useState(false);
  // the add dialog, which carries both forms - keeps you on this page
  const [adding, setAdding] = useState(false);
  // the person being edited, or null - same dialog, prefilled
  const [editing, setEditing] = useState(null);

  const { t } = useTranslation();

  /**
   * Both endpoints are fetched together and merged here. `users` is the spine
   * - every person appears exactly once, whether or not a customer record
   * exists for them.
   */
  const fetchData = async () => {
    try {
      const [usersRes, customersRes] = await Promise.all([
        fetch(`${API}/users`),
        fetch(`${API}/customers`),
      ]);

      const usersJson = await usersRes.json();
      const customersJson = await customersRes.json();

      const users = usersJson.users || [];
      const customers = customersJson.customers || customersJson || [];

      // index the customer records by the user they claim to belong to
      const byUserId = new Map();
      customers.forEach((c) => {
        if (c.user_id !== null && c.user_id !== undefined) byUserId.set(c.user_id, c);
      });

      /**
       * Newest first.
       *
       * /users comes back in alphabetical order, which is what the dropdowns
       * that read the same endpoint want - but this screen is where somebody
       * who was just added is looked for, and on a list of 114 names that
       * meant hunting for them. Sorted by id rather than by a date: the
       * endpoint sends no created_at, and ids ascend with creation.
       */
      const newestFirst = [...users].sort((a, b) => Number(b.id) - Number(a.id));

      setPeople(
        newestFirst.map((u) => {
          const customer = byUserId.get(u.id) || null;

          return {
            ...u,
            customer_id: customer ? customer.id : null,
            code: customer ? customer.code : null,
            address: customer ? customer.address : null,
            vat_name: customer ? customer.vat_name : null,
            phone_number: customer ? customer.phone_number : null,
            // a customer-role user with no customer record cannot be given a
            // code or an order - worth surfacing, not hiding
            missing_profile: u.role_id === "customer" && !customer,
          };
        })
      );
    } catch (e) {
      setPeople([]);
    }
    setloading(false);
  };

  useEffect(() => {
    setloading(true);
    fetchData();
  }, []);

  // role first (the real distinction), then the free-text search
  const byRole = role === "all" ? people : people.filter((p) => p.role_id === role);

  const narrowed = byRole.filter((p) => {
    if (profile === "missing" && !p.missing_profile) return false;
    if (profile === "complete" && p.missing_profile) return false;
    if (profile === "no_code" && p.code) return false;
    return true;
  });

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  const countFor = (key) =>
    key === "all" ? people.length : people.filter((p) => p.role_id === key).length;

  const incomplete = people.filter((p) => p.missing_profile).length;

  /**
   * Deletes through the same endpoint the old DeleteUser component used;
   * that component rendered its own text link, which is what made the
   * Operations column wrap.
   */
  const removeUser = async (person) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${person.name}?`)) return;

    try {
      await axios.post(
        `${API}/remove_user`,
        { id: person.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  const cell = { fontSize: 14 };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            {/* title, role chips, search, count and both buttons - one row */}
            <FilterBar
              title="Users"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={byRole.length}
              actions={
                /* user or customer is chosen inside the dialog, not out here */
                <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                  Add
                </Button>
              }
            >
              <FilterSelect
                label="Profile"
                icon="badge"
                value={profile}
                onChange={setProfile}
                anyLabel="All"
                options={[
                  { value: "complete", label: "Has customer record" },
                  { value: "missing", label: "Without profile" },
                  { value: "no_code", label: "No code" },
                ]}
              />

              {ROLE_TABS.map((tab) => (
                <Chip
                  key={tab.key}
                  size="small"
                  label={`${tab.label} (${countFor(tab.key)})`}
                  onClick={() => setRole(tab.key)}
                  variant={role === tab.key ? "filled" : "outlined"}
                  sx={({ palette, functions: { rgba } }) => ({
                    fontWeight: role === tab.key ? 700 : 500,
                    backgroundColor:
                      role === tab.key ? rgba(palette.primary.main, 0.18) : "transparent",
                    borderColor: rgba(palette.dark.main, 0.15),
                    color: role === tab.key ? palette.dark.main : palette.text.main,
                    cursor: "pointer",
                  })}
                />
              ))}

              {incomplete > 0 && (
                <Tooltip title="Customer-role users with no customer record (no code/address) - needs review">
                  <Chip
                    label={`${incomplete} without profile`}
                    size="small"
                    sx={({ palette, functions: { rgba } }) => ({
                      backgroundColor: rgba(palette.warning.main, 0.18),
                      color: palette.warning.focus,
                      fontWeight: 700,
                    })}
                  />
                </Tooltip>
              )}
            </FilterBar>

            {loading ? (
              <CargoLoader />
            ) : (
              <SuiBox sx={{ overflowX: "auto" }}>
                <>
                  <Table sx={{ minWidth: 1050 }} aria-label="users table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Name</TableCell>
                        <TableCell align="center">Email</TableCell>
                        <TableCell align="center">Mobile</TableCell>
                        <TableCell align="center">Role</TableCell>
                        <TableCell align="center">Code</TableCell>
                        <TableCell align="center">Vat Name</TableCell>
                        <TableCell align="center">Address</TableCell>
                        <TableCell align="center">{t("operations")}</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((person) => (
                        <TableRow key={person.id}>
                          <IdCell value={person.id} />
                          <TableCell align="center" sx={cell}>
                            {person.name ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {person.email ?? "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {person.mobile && person.mobile !== "0"
                              ? person.mobile
                              : person.phone_number ?? "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <Chip
                              label={person.role_id ?? "-"}
                              size="small"
                              sx={({ palette, functions: { rgba } }) => ({
                                fontWeight: 700,
                                textTransform: "capitalize",
                                backgroundColor:
                                  person.role_id === "admin"
                                    ? rgba(palette.info.main, 0.16)
                                    : rgba(palette.primary.main, 0.16),
                                color:
                                  person.role_id === "admin"
                                    ? palette.info.main
                                    : palette.dark.main,
                              })}
                            />
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            {person.code ?? "-"}
                          </TableCell>

                          {/* the trading name on the invoice - it lives on the
                              customer record, so staff rows show a dash */}
                          <TableCell align="center" sx={cell}>
                            {person.vat_name || "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            {person.address ?? "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                // one button - the dialog behind it holds both
                                // the account and the customer profile
                                {
                                  icon: "edit",
                                  label: "Edit",
                                  onClick: () => setEditing(person),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete",
                                  tone: "danger",
                                  onClick: () => removeUser(person),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && !loading && (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No results
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Pagination {...pager} />
                </>
              </SuiBox>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      {/* the list underneath keeps its filter, page and scroll position */}
      <AddDialog
        open={adding}
        entity="user"
        tabs={[
          { entity: "user", label: "Add user" },
          { entity: "customer", label: "Add customer" },
        ]}
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* editing uses the same dialog, loaded with the existing record -
          the old /edit_user/{id} page threw away your place in the list.
          Both records sit behind one button, labelled inside. */}
      <AddDialog
        open={Boolean(editing)}
        entity="user"
        tabs={[
          { entity: "user", label: "Edit user", recordId: editing?.id },
          {
            entity: "customer",
            // a customer-role user with no customer record has nothing to open
            label: editing?.customer_id ? "Edit customer" : "Edit customer (no profile)",
            recordId: editing?.customer_id,
            disabled: !editing?.customer_id,
          },
        ]}
        onClose={() => setEditing(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Users;
